import {useMemo, useState, type ReactNode} from 'react'
import {createPortal} from 'react-dom'
import {WebGLDither, type DitherParams} from '@github-ui/brand-webgl'

// Debounce window for coalescing container resizes (e.g. while images load)
// before remounting the dither.
interface UseDitherBackgroundOptions {
  className?: string
  enabled?: boolean
  params: DitherParams
}

/**
 * Portals a `WebGLDither` background into a container captured via callback ref.
 * Needed because Primer Brand's `Hero` filters out arbitrary children:
 * https://github.com/primer/brand/issues/1356
 *
 * `enabled` lives in the hook so callers can opt out without splitting their
 * render path. That keeps the effect's setup and cleanup logic in one place,
 * and lets React rerun or tear down the observer when the opt-in state changes.
 */
export function useDitherBackground({className, enabled = true, params}: UseDitherBackgroundOptions): {
  containerRef: {current: HTMLDivElement | null}
  ditherBackground: ReactNode
} {
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null)

  const containerRef = useMemo(() => {
    let current: HTMLDivElement | null = null
    return {
      get current() {
        return current
      },
      set current(value: HTMLDivElement | null) {
        if (current !== value) {
          current = value
          setContainerEl(value)
        }
      },
    }
  }, [])

  const ditherBackground: ReactNode =
    enabled && containerEl ? createPortal(<WebGLDither className={className} params={params} />, containerEl) : null

  return {containerRef, ditherBackground}
}
